import { useRef } from 'react';
import { Text } from '@mercator-gcp/grout';
import { FlexColumn } from 'components/layout/FlexColumn/FlexColumn';
import { FlexRow } from 'components/layout/FlexRow';
import ChatView from 'components/widgets/Chat/ChatView';
import FilterToolbar from 'components/widgets/FilterToolbar';
import FilterToolbarItem from 'components/widgets/FilterToolbar/ToolbarItem';
import BackButton from 'components/widgets/ProjectGroupEdit/EditFiltersStep/BackButton';
import CustomMapAreaDropdown from 'components/widgets/ProjectGroupEdit/EditFiltersStep/CustomMapAreaDropdown';
import GroupEditLeadsTable from 'components/widgets/ProjectGroupEdit/EditFiltersStep/GroupEditLeadsTable';
import GroupNameInput from 'components/widgets/ProjectGroupEdit/EditFiltersStep/GroupNameInput';
import MainMenu from 'components/widgets/ProjectGroupEdit/EditFiltersStep/MainMenu';
import SaveGroupButton from 'components/widgets/ProjectGroupEdit/EditFiltersStep/SaveGroupButton';
import NotificationSettings from 'components/widgets/ProjectGroupEdit/NotificationSettings';
import { isChatCreatedSavedSearch } from 'components/widgets/ProjectInsights/Toolbar/getSavedSearchEditorType';
import {
  CHAT_SIDEBAR_WIDTH,
  DEFAULT_SIDEBAR_WIDTH,
} from 'components/widgets/ProjectInsights/Toolbar/sidebarWidth';
import { useSelectedGroupConfig } from 'components/widgets/ProjectInsights/Toolbar/useSelectedGroupConfig';
import { useSavedSearchChatSession } from 'hooks/useSavedSearchChatSession';
import { useDashboardGroupType } from 'state/dashboard';
import { GROUP_TYPE } from 'utils/constants';

const containerStyle = {
  height: '100%',
};

const toolbarStyle = {
  alignItems: 'baseline',
  height: '100%',
  position: 'relative',
  boxShadow: '2px 0px 7px 0px #0000001F',
};

const chatEditorColumnStyle = {
  height: '100%',
  width: '100%',
};

const headerContainer = {
  alignItems: 'flex-start',
};

const rightPanelStyle = {
  width: '100%',
  height: '100%',
  overflowX: 'hidden',
  flex: 1,
  paddingX: 'padding_32',
  paddingTop: 'padding_16',
  paddingBottom: 'padding_24',
};

const leadContainerStyle = {
  height: '100%',
  width: '100%',
};

const saveButtonStyle = {
  width: '100%',
};

const nameInputStyle = {
  marginTop: 'padding_16',
  marginBottom: 'padding_8',
  alignItems: 'flex-start',
  justifyContent: 'flex-start',
  gap: 'padding_8',
};

const EditFiltersStep = () => {
  const saveButtonRef = useRef<HTMLButtonElement>(null);
  const [groupType] = useDashboardGroupType();
  const selectedGroupConfig = useSelectedGroupConfig();
  const isChatEditor = isChatCreatedSavedSearch(selectedGroupConfig);
  const { session } = useSavedSearchChatSession(selectedGroupConfig?.adk_session_id);

  const toolbarHeader = (
    <>
      <FilterToolbarItem>
        <BackButton />
      </FilterToolbarItem>
      <FilterToolbarItem>
        <NotificationSettings />
      </FilterToolbarItem>
    </>
  );

  return (
    <FlexRow sx={containerStyle}>
      <FlexRow.Left
        sx={{
          ...toolbarStyle,
          width: isChatEditor ? CHAT_SIDEBAR_WIDTH : DEFAULT_SIDEBAR_WIDTH,
        }}
      >
        {isChatEditor ? (
          <FlexColumn sx={chatEditorColumnStyle}>
            {toolbarHeader}
            {session && (
              <ChatView
                chat={session}
                showFooter={false}
                inputPlaceholder="Ask Scout to refine this search..."
              />
            )}
          </FlexColumn>
        ) : (
          <FilterToolbar
            showCompaniesFilter={groupType !== GROUP_TYPE.COMPANY}
            headerElement={toolbarHeader}
            extraFilters={<CustomMapAreaDropdown />}
            footerElement={<SaveGroupButton sx={saveButtonStyle} />}
          />
        )}
      </FlexRow.Left>
      <FlexRow.Right sx={rightPanelStyle}>
        <FlexColumn sx={leadContainerStyle}>
          <FlexRow sx={headerContainer}>
            <Text.Lead sx={{ fontWeight: 600 }}>Edit Saved Search Filters</Text.Lead>
            <MainMenu saveButtonRef={saveButtonRef} isChatEditor={isChatEditor} />
          </FlexRow>
          <FlexRow sx={nameInputStyle}>
            <Text.SmallLabel sx={{ marginTop: '8px' }}>Search Name</Text.SmallLabel>
            <GroupNameInput
              onEnter={() => {
                saveButtonRef.current?.click();
              }}
            />
          </FlexRow>
          <GroupEditLeadsTable />
        </FlexColumn>
      </FlexRow.Right>
    </FlexRow>
  );
};

export default EditFiltersStep;
